import React from "react";
import { Button, Drawer, Text } from "braid-design-system";
import { searchTransactionDocuments } from "../../services/transactionService";

type TransactionHistoryViewerProps = {
    documents: Array<Record<string, any>>;
    setupNo: number;
    setupTitle: string;
    onLoadDocument: (documentNo: string) => void;
};

const SEARCH_PAGE_SIZE = 25;

const getDocumentNo = (document: Record<string, any>): string => (
    String(
        document.trhed_runid
        ?? document.trhedRunId
        ?? document.DocNo
        ?? document.docNo
        ?? "",
    ).trim()
);

const getRunId = (document: Record<string, any>): string => (
    String(
        document.trhed_runid
        ?? document.trhedRunId
        ?? "",
    ).trim()
);

const getDocNoField = (document: Record<string, any>): string => (
    String(
        document.DocNo
        ?? document.docNo
        ?? "",
    ).trim()
);

const getDocAndRunDisplay = (document: Record<string, any>): string => {
    const docNo = getDocNoField(document);
    const runId = getRunId(document);

    if (docNo && runId) {
        return `${docNo} (${runId})`;
    }

    return docNo || runId || "-";
};

const getTransactionType = (document: Record<string, any>): string => (
    String(
        document.trhed_type
        ?? document.trhedType
        ?? document.type
        ?? "Transaction",
    ).trim() || "Transaction"
);

const getAmount = (document: Record<string, any>): string => {
    const value = document.trhed_netamount
        ?? document.trhedNetAmount
        ?? document.trhed_invamt
        ?? document.trhedInvAmt
        ?? document.net
        ?? document.amount
        ?? 0;

    const parsed = typeof value === "number" ? value : parseFloat(String(value ?? "0"));
    return Number.isFinite(parsed)
        ? parsed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "0.00";
};

// trhed_date carries the document date and trhed_time the save time; both are
// DATETIME columns, so take the date part of one and the time part of the other.
const combineDateAndTime = (document: Record<string, any>): string => {
    const dateValue = String(document.trhed_date ?? document.trhedDate ?? document.date ?? "").trim();
    const timeValue = String(document.trhed_time ?? document.trhedTime ?? document.time ?? "").trim();
    const datePart = dateValue.split(/[T ]/)[0];
    const timeMatch = timeValue.match(/\d{2}:\d{2}(:\d{2})?/);
    if (!datePart) return "";
    // A bare "YYYY-MM-DD" parses as UTC; always pass a time so it stays local.
    return `${datePart} ${timeMatch ? timeMatch[0] : "00:00:00"}`;
};

const getTransactionDate = (document: Record<string, any>): string => {
    const rawValue = combineDateAndTime(document);

    if (!rawValue) {
        return "-";
    }

    const parsed = new Date(rawValue);
    if (Number.isNaN(parsed.getTime())) {
        return rawValue;
    }

    return parsed.toLocaleString();
};

const getTransactionTimestamp = (document: Record<string, any>): number => {
    const parsed = new Date(combineDateAndTime(document));

    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const getNewestDocument = (documents: Array<Record<string, any>>): Record<string, any> | null => {
    if (!documents.length) {
        return null;
    }

    return [...documents].sort((left, right) => {
        const timeDifference = getTransactionTimestamp(right) - getTransactionTimestamp(left);
        if (timeDifference !== 0) {
            return timeDifference;
        }

        return getDocumentNo(right).localeCompare(getDocumentNo(left), undefined, { numeric: true });
    })[0];
};

const TransactionHistoryViewer: React.FC<TransactionHistoryViewerProps> = ({
    documents,
    setupNo,
    setupTitle,
    onLoadDocument,
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [isSearchMode, setIsSearchMode] = React.useState(false);
    const [searchText, setSearchText] = React.useState("");
    const [dateFrom, setDateFrom] = React.useState("");
    const [dateTo, setDateTo] = React.useState("");
    const [statusFilter, setStatusFilter] = React.useState<"all" | "temporary" | "processed">("all");
    const [amountMin, setAmountMin] = React.useState("");
    const [amountMax, setAmountMax] = React.useState("");
    const [searchOffset, setSearchOffset] = React.useState(0);
    const [searchResults, setSearchResults] = React.useState<Array<Record<string, any>>>([]);
    const [isSearching, setIsSearching] = React.useState(false);
    const [searchError, setSearchError] = React.useState<string | null>(null);
    const [hasSearched, setHasSearched] = React.useState(false);
    const latestDocument = getNewestDocument(documents);
    const latestDocumentNo = latestDocument ? getDocumentNo(latestDocument) : "";
    const latestDocumentDisplay = latestDocument ? getDocAndRunDisplay(latestDocument) : "";
    const displayedDocuments = isSearchMode ? searchResults : documents;
    const tableEmptyMessage = isSearchMode
        ? hasSearched ? "No matching transaction documents found." : "Use the filters above to search older documents."
        : "No recent transaction documents available.";

    const executeSearch = async (nextOffset: number = 0) => {
        const parsedAmountMin = amountMin.trim() ? parseFloat(amountMin) : undefined;
        const parsedAmountMax = amountMax.trim() ? parseFloat(amountMax) : undefined;

        try {
            setIsSearching(true);
            setSearchError(null);
            const results = await searchTransactionDocuments(setupNo, {
                q: searchText.trim() || undefined,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
                status: statusFilter,
                amount_min: Number.isFinite(parsedAmountMin) ? parsedAmountMin : undefined,
                amount_max: Number.isFinite(parsedAmountMax) ? parsedAmountMax : undefined,
                limit: SEARCH_PAGE_SIZE,
                offset: nextOffset,
            });
            setSearchResults(results);
            setSearchOffset(nextOffset);
            setHasSearched(true);
        } catch (error: any) {
            setSearchError(error?.message || "Failed to search transaction documents.");
            setSearchResults([]);
            setHasSearched(true);
        } finally {
            setIsSearching(false);
        }
    };

    const openSearchMode = () => {
        setIsSearchMode(true);
        if (!hasSearched) {
            executeSearch(0);
        }
    };

    const resetSearch = () => {
        setSearchText("");
        setDateFrom("");
        setDateTo("");
        setStatusFilter("all");
        setAmountMin("");
        setAmountMax("");
        setSearchOffset(0);
        setSearchResults([]);
        setSearchError(null);
        setHasSearched(false);
    };

    return (
        <section className="transaction-history-viewer" aria-label="Transaction history viewer">
            {latestDocument ? (
                <div className="transaction-history-summary">
                        <button
                        type="button"
                        className="transaction-history-link"
                        onClick={() => onLoadDocument(latestDocumentNo)}
                    >
                        Last transaction: {latestDocumentDisplay} {getAmount(latestDocument)} on {getTransactionDate(latestDocument)}
                    </button>
                    <span className="transaction-history-separator" aria-hidden="true">-&gt;</span>
                    <button
                        type="button"
                        className="transaction-history-list-link"
                        onClick={() => setIsOpen(true)}
                    >
                        list last 10
                    </button>
                </div>
            ) : (
                <div className="transaction-history-summary">
                    <span className="transaction-history-empty">No recent transactions found.</span>
                    <button
                        type="button"
                        className="transaction-history-list-link"
                        onClick={() => setIsOpen(true)}
                    >
                        open history
                    </button>
                </div>
            )}

            <Drawer
                title="Transactions List View"
                open={isOpen}
                onClose={() => setIsOpen(false)}
                width="large"
            >
                <div className="transaction-history-drawer">
                    <div className="transaction-history-drawer-heading">
                        <Text tone="secondary">{setupTitle}</Text>
                        <div className="transaction-history-mode-actions">
                            <Button
                                size="small"
                                variant={isSearchMode ? "ghost" : "solid"}
                                tone="brandAccent"
                                onClick={() => setIsSearchMode(false)}
                            >
                                Last 10
                            </Button>
                            <Button
                                size="small"
                                variant={isSearchMode ? "solid" : "ghost"}
                                tone="brandAccent"
                                onClick={openSearchMode}
                            >
                                Search older documents
                            </Button>
                        </div>
                    </div>

                    {isSearchMode ? (
                        <form
                            className="transaction-history-search"
                            onSubmit={(event) => {
                                event.preventDefault();
                                executeSearch(0);
                            }}
                        >
                            <label className="transaction-history-search-field transaction-history-search-field-wide">
                                <span>Document, member, or supplier</span>
                                <input
                                    value={searchText}
                                    onChange={(event) => setSearchText(event.target.value)}
                                    placeholder="Search document number"
                                />
                            </label>
                            <label className="transaction-history-search-field">
                                <span>From</span>
                                <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
                            </label>
                            <label className="transaction-history-search-field">
                                <span>To</span>
                                <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
                            </label>
                            <label className="transaction-history-search-field">
                                <span>Status</span>
                                <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as "all" | "temporary" | "processed")}>
                                    <option value="all">All</option>
                                    <option value="temporary">Temporary</option>
                                    <option value="processed">Processed</option>
                                </select>
                            </label>
                            <label className="transaction-history-search-field">
                                <span>Min amount</span>
                                <input type="number" min="0" step="0.01" value={amountMin} onChange={(event) => setAmountMin(event.target.value)} />
                            </label>
                            <label className="transaction-history-search-field">
                                <span>Max amount</span>
                                <input type="number" min="0" step="0.01" value={amountMax} onChange={(event) => setAmountMax(event.target.value)} />
                            </label>
                            <div className="transaction-history-search-actions">
                                <Button type="submit" size="small" variant="solid" tone="brandAccent" loading={isSearching}>
                                    Search
                                </Button>
                                <Button type="button" size="small" variant="ghost" onClick={resetSearch}>
                                    Clear
                                </Button>
                            </div>
                        </form>
                    ) : null}

                    {searchError ? (
                        <div className="transaction-history-search-error">{searchError}</div>
                    ) : null}

                    <div className="transaction-history-table-wrap">
                        <table className="transaction-history-table">
                            <thead>
                                <tr>
                                    <th>Transaction Number</th>
                                    <th>Transaction Type</th>
                                    <th>Amount</th>
                                    <th>Transaction Date</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedDocuments.length > 0 ? (
                                    displayedDocuments.map((document) => {
                                        const documentNo = getDocumentNo(document);

                                        return (
                                            <tr key={documentNo || JSON.stringify(document)}>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="transaction-history-table-link"
                                                        onClick={() => {
                                                            onLoadDocument(documentNo);
                                                            setIsOpen(false);
                                                        }}
                                                    >
                                                        {getDocAndRunDisplay(document)}
                                                    </button>
                                                </td>
                                                <td>{getTransactionType(document)}</td>
                                                <td>{getAmount(document)}</td>
                                                <td>{getTransactionDate(document)}</td>
                                                <td>
                                                    <Button
                                                        size="small"
                                                        variant="ghost"
                                                        onClick={() => {
                                                            onLoadDocument(documentNo);
                                                            setIsOpen(false);
                                                        }}
                                                    >
                                                        Load
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={5} className="transaction-history-empty-cell">
                                            {tableEmptyMessage}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {isSearchMode ? (
                        <div className="transaction-history-pagination">
                            <Button
                                size="small"
                                variant="ghost"
                                onClick={searchOffset <= 0 || isSearching ? undefined : () => executeSearch(Math.max(0, searchOffset - SEARCH_PAGE_SIZE))}
                            >
                                Previous
                            </Button>
                            <span>Showing {searchOffset + 1}-{searchOffset + displayedDocuments.length}</span>
                            <Button
                                size="small"
                                variant="ghost"
                                onClick={displayedDocuments.length < SEARCH_PAGE_SIZE || isSearching ? undefined : () => executeSearch(searchOffset + SEARCH_PAGE_SIZE)}
                            >
                                Next
                            </Button>
                        </div>
                    ) : null}
                </div>
            </Drawer>
        </section>
    );
};

export default TransactionHistoryViewer;
